import { ref, watch, reactive } from 'vue'
import { Project, EmptyProject } from '../types'
import { Pagination, Sorting } from '../../../data/pages/projects'
import api from '../../../services/api'
import { getOrganizationProjects } from '../../../services/api/projects'
import { useAuthStore } from '../../../stores/auth'
import { useToast } from 'vuestic-ui'

export const useProjects = (options?: { pagination?: Pagination; sorting?: Sorting }) => {
  const isLoading = ref(false)
  const projects = ref<Project[]>([])
  const error = ref<Error | null>(null)
  const { init: notify } = useToast()

  const { pagination: defaultPagination, sorting: defaultSorting } = options || {}

  const pagination = reactive<Pagination>(defaultPagination || { page: 1, perPage: 10, total: 0 })
  const sorting = reactive<Sorting>(defaultSorting || { sortBy: 'created_at', sortingOrder: 'desc' })

  const authStore = useAuthStore()

  const fetchProjects = async () => {
    const organizationId = authStore.organizationId
    if (!organizationId) {
      console.warn('[useProjects] No organizationId, skipping fetch.')
      projects.value = []
      pagination.total = 0
      return
    }

    isLoading.value = true
    error.value = null
    try {
      projects.value = await getOrganizationProjects(organizationId, {
        skip: (pagination.page - 1) * pagination.perPage,
        limit: pagination.perPage,
        sort_by: sorting.sortBy || undefined,
        sort_order: sorting.sortingOrder || undefined,
      })
      pagination.total = projects.value.length
    } catch (err: any) {
      console.error('[useProjects] Failed to fetch projects:', err)
      error.value = err
      projects.value = []
      pagination.total = 0
      notify({ message: err.response?.data?.detail || err.message || 'Failed to load projects', color: 'danger' })
    } finally {
      isLoading.value = false
    }
  }

  const updateProject = async (project: Project) => {
    const organizationId = authStore.organizationId
    if (!organizationId) {
      throw new Error('Organization ID is required to update a project.')
    }
    isLoading.value = true
    error.value = null
    try {
      const payload = {
        name: project.name,
        description: project.description,
        archived: project.archived,
      }
      const response = await api.put(`/org/${organizationId}/odk-projects/${project.id}`, payload)
      const index = projects.value.findIndex((p) => p.id === project.id)
      if (index !== -1) {
        projects.value[index] = { ...projects.value[index], ...response.data }
      }
      notify({ message: `Project "${response.data.name}" updated.`, color: 'success' })
    } catch (err: any) {
      console.error('Failed to update project:', err)
      error.value = err
      notify({ message: err.response?.data?.detail || err.message || 'Failed to update project', color: 'danger' })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const addProject = async (project: EmptyProject) => {
    const organizationId = authStore.organizationId
    if (!organizationId) {
      throw new Error('Organization ID is required to create a project.')
    }
    isLoading.value = true
    error.value = null
    try {
      const payload = {
        name: project.name,
        server_id: project.server_id,
        description: project.description,
      }

      if (payload.server_id === undefined || payload.server_id === null) {
        throw new Error('ODK Server must be selected.')
      }

      const response = await api.post(`/org/${organizationId}/odk-projects/`, payload)

      projects.value.unshift(response.data)
      pagination.total++
      notify({ message: `Project "${response.data.name}" created.`, color: 'success' })
    } catch (err: any) {
      console.error('Failed to add project:', err)
      error.value = err
      notify({ message: err.response?.data?.detail || err.message || 'Failed to add project', color: 'danger' })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const removeProject = async (project: Project) => {
    const organizationId = authStore.organizationId
    if (!organizationId) {
      throw new Error('Organization ID is required to delete a project.')
    }
    isLoading.value = true
    error.value = null
    try {
      // Отправляем запрос на удаление
      const response = await api.delete(`/org/${organizationId}/odk-projects/${project.id}`)

      // Удаляем проект из локального списка
      projects.value = projects.value.filter((p) => p.id !== project.id)
      pagination.total--

      // Показываем сообщение об успехе на основе ответа API
      if (response.data && response.data.message) {
        notify({ message: response.data.message, color: 'success' })
      } else {
        // Fallback сообщение для обратной совместимости
        const message = project.isImported
          ? `Project "${project.name}" was deleted from local database only.`
          : `Project "${project.name}" was deleted from both local database and ODK Central.`
        notify({ message, color: 'success' })
      }
    } catch (err: any) {
      console.error('Failed to delete project:', err)
      error.value = err
      notify({ message: err.response?.data?.detail || err.message || 'Failed to delete project', color: 'danger' })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Watch for changes and refetch
  watch(
    [() => pagination.page, () => pagination.perPage, sorting],
    () => {
      // console.log('[useProjects] Pagination or sorting changed, refetching...');
      fetchProjects()
    },
    { deep: true },
  )

  watch(
    () => authStore.organizationId,
    (newOrgId) => {
      // console.log(`[useProjects] Organization changed from ${oldOrgId} to ${newOrgId}, refetching...`);
      if (newOrgId) {
        fetchProjects()
      } else {
        // console.warn('[useProjects] New organizationId is empty, clearing projects');
        projects.value = []
        pagination.total = 0
      }
    },
    { immediate: true },
  )

  return {
    isLoading,
    projects,
    pagination,
    sorting,
    error,
    fetchProjects,
    updateProject,
    addProject,
    removeProject,
  }
}
